/* eslint-disable max-len */
import { TableBody, TableHead, TableRow, Icon } from "@mui/material";
import { useState, useRef, useEffect, useContext } from "react";
import theme from "../styles/theme";
import { ClickableIcon } from "./Icon";
import TableComponent, {
  StyledBodyCell,
  StyledHeaderCell,
  StyledTableRow,
} from "./Table";
import ConfirmModal from "./ConfirmModal";
import Button from "./Button";
import { EmptyVersion, VersionType } from "../types/Version";
import { VersionContext } from "../context/VersionContext";
import VersionCreate from "./VersionCreate";
import axios from "axios";
import SnackBar from "./SnackBar";
import VersionDropdown from "./VersionDropdown";
import FlexBox, { FlexGrow } from "./FlexBox";
import styled from "styled-components";
import VersionEdit from "./VersionEdit";
import DownloadModal from "./DownloadModal";
import ImportModal from "./exportImport/importModal";
import { UserContext } from "../context/UserContext";
import { ProgramSelectModal } from "./ProgramSelectModal";
import { UniProgSelect } from "./UniProgSelect";
// import { export} from "../assets/ExportedCurriculumData.zip"
interface Column {
  id:
  | "title"
  | "university"
  | "program"
  | "status"
  | "version_number"
  | "display_version"
  | "action"
  | "adopted_date";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}
type AllVersionsListType = {
  mode: "view" | "edit" | "select";
  isSuperUser?: boolean;
  data: Array<any>;
  fetchParent: (university: string, program: string) => void;
  onSelect: (data: VersionType) => void;
  onDeselect: (data: VersionType) => void;
  onDelete: (data: VersionType) => void;
  onAdopt: (data: VersionType) => void;
  onDraft: (data: VersionType) => void;
};
const CustomBox = styled(FlexBox)`
  margin-bottom: 16px;
  background: white;
  padding: 16px;
  border-radius: 5px;
  box-shadow: 0 0 10px ${theme.color.lightBlueGray300};
`;
const RowStyle = {
  "&:hover": {
    backgroundColor: "wheat !important",
    color: "white",
    cursor: "pointer",
    fontFamily: "Poppins",
    ".MuiTableCell-body": {
      color: "white",
    },
  },
};
const RowSelectedStyle = {
  "&:hover": {
    backgroundColor: "red !important",
    color: "white",
    cursor: "pointer",
    fontFamily: "Poppins",
    ".MuiTableCell-body": {
      color: "white",
    },
  },
};
function AllVersionsList({
  data,
  mode,
  isSuperUser,
  fetchParent,
  onSelect,
  onDeselect,
  onDelete,
  onAdopt,
  onDraft,
}: AllVersionsListType) {
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isAdoptOpen, setIsAdoptOpen] = useState<boolean>(false);
  const [isDraftOpen, setIsDraftOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isDeselectOpen, setIsDeselectOpen] = useState<boolean>(false);
  const [selectedVersion, setSelectedVersion] =
    useState<VersionType>(EmptyVersion);
  const [expandedSections, setExpandedSections] = useState<{
    historical: boolean;
    active: boolean;
    legacy: boolean;
    draft: boolean;
  }>({
    historical: false,
    active: true,
    legacy: true,
    draft: false,
  });
  const { version } = useContext(VersionContext);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isErrorOpen, setIsErrorOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const versionContext = useContext(VersionContext);
  const [columns, setColumns] = useState<Array<Column>>([
    { id: "title", label: "Title", minWidth: 200 },
    { id: "status", label: "Status", minWidth: 100 },

    { id: "university", label: "University", minWidth: 100 },
    { id: "program", label: "Program", minWidth: 100 },
    {
      id: "adopted_date",
      label: "Adopted Date",
      minWidth: 100,
    },
  ]);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(() => {
    return localStorage.getItem("selectedProgram") || "";
  });
  const [selectedProgramId, setSelectedProgramId] = useState(() => {
    return localStorage.getItem("selectedProgId") || "";
  });
  const [selectedUniversity, setSelectedUniversity] = useState(() => {
    return localStorage.getItem("university") || "";
  });
  const [showModal, setShowModal] = useState(
    !selectedProgram || !selectedUniversity
  );

  const handleProgramSelect = (program: string) => {
    localStorage.setItem("selectedProgram", program);
    setSelectedProgram(program);
    setShowModal(false);
  };

  const universityCode = localStorage.getItem("university");
  const userContext = useContext(UserContext);
  const role = userContext.user.role;
  useEffect(() => {
    setSelectedVersion(version);
  }, [version]);
  const createVersion = (fdata: any) => {
    setLoading(true);
    setIsCreateOpen(false);
    document.body.classList.add("loading-cursor");

    axios
      .post("/version/new", fdata)
      .then((res) => {
        document.body.classList.remove("loading-cursor");
        setMessage("New version created successfully!");
        setIsSnackbarOpen(true);
        setLoading(false);
        fetchParent(selectedUniversity, selectedProgram);
        setIsCreateOpen(false);
      })
      .catch(function (error) {
        document.body.classList.remove("loading-cursor");
        setLoading(false);

        if (error.response) {
          setErrorMessage(
            error.response.data.error || "Failed to create new version!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setErrorMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
      });
  };
  const editVersion = (fdata: any) => {
    const data = {
      core_credits: parseInt(fdata.core_credits),
      hss_credits: parseInt(fdata.hss_credits),
      math_credits: parseInt(fdata.math_credits),
      max_credits: parseInt(fdata.max_credits),
      min_credits: parseInt(fdata.min_credits),
      rnd_credits: parseInt(fdata.rnd_credits),
      xp_credits: parseInt(fdata.xp_credits),
      title: fdata.title,
      version_number: selectedVersion.version_number,
    };

    axios
      .put("/version/update", data)
      .then((res) => {
        setMessage("Updated Version successfully!");
        setIsSnackbarOpen(true);
        setLoading(false);
        fetchParent(selectedUniversity, selectedProgram);
        setIsEditOpen(false);
        setSelectedVersion(EmptyVersion);
      })
      .catch(function (error) {
        if (error.response) {
          setErrorMessage(
            error.response.data.error || "Failed to update the version!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setErrorMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
      });
  };
  const deleteVersion = (version: VersionType) => {
    setLoading(true);
    document.body.classList.add("loading-cursor");

    axios
      .delete(`/version/delete?version_number=${version.version_number}`)
      .then((res) => {
        if (res.data.isDeleted) {
          document.body.classList.remove("loading-cursor");
          versionContext.setCurrentVersion(EmptyVersion);
          setSelectedVersion(EmptyVersion);
          setMessage("Deleted successfully!");
          setLoading(false);
          setIsSnackbarOpen(true);
          fetchParent(selectedUniversity, selectedProgram);
        }
        fetchParent(selectedUniversity, selectedProgram);
        setLoading(false);
      })
      .catch(function (error) {
        document.body.classList.remove("loading-cursor");
        setLoading(false);
        if (error.response) {
          setErrorMessage(
            error.response.data.error || "Failed to delete version!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setErrorMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
      });
  };
  const adoptVersion = (version: VersionType) => {
    axios
      .put("/version/adopt", { version_number: version.version_number, source_program_id: selectedProgramId })
      .then((res) => {
        if (res.data.IsAdopted) {
          versionContext.setCurrentVersion(EmptyVersion);
          setSelectedVersion(EmptyVersion);
          setMessage("Adopted version to active successfully!");
          setIsSnackbarOpen(true);
          setLoading(false);
          fetchParent(selectedUniversity, selectedProgram);
        }
        fetchParent(selectedUniversity, selectedProgram);
        setLoading(false);
      })
      .catch(function (error) {
        if (error.response) {
          setErrorMessage(
            error.response.data.error ||
            "Failed to adopt the selected draft curriculum version version!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setErrorMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
      });
  };

  const revertVersion = (version: VersionType) => {
    axios
      .put("/version/revert", { version_number: version.version_number })
      .then((res) => {
        if (res.data.IsReverted) {
          versionContext.setCurrentVersion(EmptyVersion);
          setSelectedVersion(EmptyVersion);
          setMessage("Reverted version to draft successfully!");
          setIsSnackbarOpen(true);
          setLoading(false);
          fetchParent(selectedUniversity, selectedProgram);
        }
        fetchParent(selectedUniversity, selectedProgram);
        setLoading(false);
      })
      .catch(function (error) {
        if (error.response) {
          setErrorMessage(
            error.response.data.error ||
            "Failed to revert the selected active curriculum version to draft!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setErrorMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
      });
  };

  const CreateUrl = () => {
    const body = {
      curriculum_version: version.version_number,
      university_code: universityCode,
      //   pillar_prefix: selectedPrefix.value,
    };
    axios
      .post("/version/export", body)
      .then((res: any) => {
        // handleDownload(res.data.download_url);
        setDownloadUrl(res.data.download_url);
        setDownloadOpen(true);
      })
      .catch((err: any) => {
        setIsErrorOpen(true);
        setErrorMessage(err?.response?.data?.error);
      });
  };

  const handleDownload = (url: string) => {
    setLoading(true);
    const filename = url.substring(url.lastIndexOf("/") + 1);
    // window.open('http://localhost:3000/ExportedCurriculumData.zip')
    // fetch("http://localhost:3000/ExportedCurriculumData_V3_231117131133.zip")
    fetch(
      `${process.env.REACT_APP_DOC_URL?.replace(/\/$/, "")}/${url?.replace(
        /^\//,
        ""
      )}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
        setIsSnackbarOpen(true);
        setMessage("Downloaded Successfully!");
        setDownloadOpen(false);
      })
      .catch((error) => {
        setIsErrorOpen(true);
        setDownloadOpen(false);
        setErrorMessage("There has been a problem with your fetch operation");
        console.error(
          "There has been a problem with your fetch operation:",
          error
        );
      });
    setLoading(false);
  };

  const toggleSection = (section: 'historical' | 'active' | 'legacy' | 'draft') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const groupedData = {
    historical: data.filter((row: any) => row.status === "Historical"),
    legacy: data.filter((row: any) => row.status === "Active Legacy"),
    active: data.filter((row: any) => row.status === "Active Prime"),
    draft: data.filter((row: any) => row.status === "Draft"),
  };

  const renderSectionHeader = (
    title: string,
    count: number,
    isExpanded: boolean,
    icon: string,
    fontColor: string,
    background: string,
    border: string,
    onToggle: () => void
  ) => (
    <StyledTableRow
      onClick={onToggle}
      style={{
        backgroundColor: theme.color.lightBlueGray100,
        color: fontColor,
        cursor: "pointer",
        fontWeight: "bold",
        border:'5px solid black'
      }}
    >
      <StyledBodyCell colSpan={columns.length} style={{ paddingLeft: "16px", background: background,  borderTop:`1px solid ${border}` }}>
        <FlexBox style={{ alignItems: "center", gap: "8px", fontWeight: 'bold', color: fontColor }}>
          <Icon 
            style={{ 
              marginRight: 20, 
              color: theme.color.blue900,
              backgroundColor: '#E3F2FD',
              borderRadius: '4px',
              // padding: '0px',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.color.blue900}`,
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.backgroundColor = theme.color.blue900;
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.backgroundColor = '#E3F2FD';
              e.currentTarget.style.color = theme.color.blue900;
            }}
            onClick={(e: any) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {isExpanded ? "remove" : "add"}
          </Icon>
          {/* <Icon style={{ marginRight: 0, color:'#000000ff' }}>{icon}</Icon> */}
          <span>{title} ({count})</span>
        </FlexBox>
      </StyledBodyCell>
    </StyledTableRow>
  );

  const renderVersionRows = (versions: any[]) => {
    return versions.map((row: any, index) => (
      <StyledTableRow
        hover
        role="checkbox"
        tabIndex={-1}
        onClick={() => setSelectedVersion(row)}
        onDoubleClick={() => setIsEditOpen(true)}
        selected={selectedVersion.version_number === row.version_number}
        key={`${row.version_number}-${index}`}
      >
        {columns.map((column) => {
          let value;
          if (column.id === "program") {
            value = localStorage.getItem("selectedProgram");
          } else if (column.id === "title") {
            // Combine display_version and title
            value = `${row.display_version} - ${row.title}`;
          } else {
            value = row[column.id];
          }
          
          return (
            <StyledBodyCell style={{paddingLeft: column.id === "title" ? 80 : ""}} key={column.id} align={column.align}>
              {column.format && typeof value === "number"
                ? column.format(value)
                : value}
            </StyledBodyCell>
          );
        })}
      </StyledTableRow>
    ));
  };

  if (showModal) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: "60vh" }}>
        <UniProgSelect
          fetchParent={fetchParent}
          onSelect={() => handleProgramSelect}
          isOpen={showModal}
          setOpen={() => { }}
        />
      </div>
    );
  }
  return (
    <>
      <SnackBar
        isOpen={isSnackbarOpen}
        setOpen={setIsSnackbarOpen}
        message={message}
        type="success"
      />
      <SnackBar
        isOpen={isErrorOpen}
        setOpen={setIsErrorOpen}
        header="Error!"
        message={errorMessage}
        type="error"
      />
      <DownloadModal
        setErrorMessage={setErrorMessage}
        setIsErrorOpen={setIsErrorOpen}
        loading={loading}
        url={downloadUrl}
        isOpen={downloadOpen}
        setOpen={setDownloadOpen}
        confirmFunction={() => handleDownload(downloadUrl)}
      />
      <ImportModal
        loading={loading}
        setLoading={setLoading}
        setIsSnackbarOpen={setIsSnackbarOpen}
        setIsErrorOpen={setIsErrorOpen}
        setMessage={setMessage}
        setErrorMessage={setErrorMessage}
        title="Import Pillars into Version"
        isOpen={importOpen}
        setOpen={setImportOpen}
        confirmFunction={() => handleDownload(downloadUrl)}
      />
      <ConfirmModal
        isOpen={isAdoptOpen}
        setOpen={setIsAdoptOpen}
        confirmFunction={() => {
          adoptVersion(selectedVersion);
          setIsAdoptOpen(false);
        }}
        title={"Adopt Version to Active"}
        description={
          "Are you sure you want to adopt this draft curriculum version to active?"
        }
      />
      <ConfirmModal
        isOpen={isDraftOpen}
        setOpen={setIsDraftOpen}
        confirmFunction={() => {
          revertVersion(selectedVersion);
          setIsDraftOpen(false);
        }}
        title={"Revert Version to Draft"}
        description={
          "Are you sure you want to revert this active curriculum version to draft?"
        }
      />
      <ConfirmModal
        isOpen={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        confirmFunction={() => {
          deleteVersion(selectedVersion);
          setIsDeleteOpen(false);
        }}
        title={"Delete Version"}
        description={"Are you sure you want to delete this curriculum version?"}
      />
      <ConfirmModal
        isOpen={isSelectOpen}
        setOpen={setIsSelectOpen}
        confirmFunction={() => {
          onSelect(selectedVersion);
          setIsSelectOpen(false);
        }}
        title={"Select curriculum version"}
        description={
          "Are you sure you want to select this curriculum version for session?"
        }
      />
      <ConfirmModal
        isOpen={isDeselectOpen}
        setOpen={setIsDeselectOpen}
        confirmFunction={() => {
          onDeselect(selectedVersion);
          setIsDeselectOpen(false);
        }}
        title={"Deselect curriculum version"}
        description={
          "Are you sure you want to deselect this curriculum version for session?"
        }
      />
      {isCreateOpen && (
        <VersionCreate
          isSuperUser={isSuperUser}
          header="Create Version"
          mode="create"
          isOpen={isCreateOpen}
          setOpen={setIsCreateOpen}
          onSaveComplete={(data) => {
            createVersion(data);
          }}
        />
      )}
      {isEditOpen && (
        <VersionEdit
          isDisableButton={
            selectedVersion.title === "" ||
            selectedVersion.university === "" ||
            selectedVersion.status !== "Draft" ||
            role === "instructor"
          }
          header="Edit Version"
          mode="modify"
          isOpen={isEditOpen}
          versionData={selectedVersion}
          setOpen={setIsEditOpen}
          onSaveComplete={(data) => {
            editVersion(data);
          }}
        />
      )}
      {!showModal && (
        <CustomBox>
          <FlexGrow style={{ flexGrow: 0 }}>
            {version &&
              version.version_number &&
              version.university !== "" &&
              selectedVersion.version_number === version.version_number ? (
              <Button
                className="reduce_opct_onhover"
                onClick={() => setIsDeselectOpen(true)}
                style={{
                  width: 110,
                  whiteSpace: "nowrap",
                  background: theme.color.editButton,
                  color: "white",
                  boxShadow: "0 2px 5px #808080cc",
                  border: "none",
                }}
              >
                <Icon fontSize="small" style={{ paddingRight: 10 }}>
                  close_icon
                </Icon>
                Deselect
              </Button>
            ) : (
              <Button
                className="reduce_opct_onhover"
                onClick={() => setIsSelectOpen(true)}
                disabled={
                  selectedVersion.title === "" ||
                  selectedVersion.university === ""
                }
                style={{
                  width: 110,
                  whiteSpace: "nowrap",
                  background: theme.color.blueButton,
                  color: "white",
                  boxShadow: "0 2px 5px #808080cc",
                  border: "none",
                }}
              >
                <Icon fontSize="small" style={{ paddingRight: 10 }}>
                  done_icon
                </Icon>
                Select
              </Button>
            )}
          </FlexGrow>
          {role !== "instructor" && (
            <>
              <FlexGrow style={{ flexGrow: 0 }}>
                <Button
                  className={`${!(
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Draft"
                  ) && "reduce_opct_onhover"
                    }`}
                  onClick={() => setIsAdoptOpen(true)}
                  disabled={
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Draft"
                  }
                  style={{
                    whiteSpace: "nowrap",
                    width: 110,
                    background: theme.color.greenButton,
                    color: "white",
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    publish_icon
                  </Icon>
                  Adopt
                </Button>
              </FlexGrow>
              <FlexGrow style={{ flexGrow: 0 }}>
                <Button
                  className={`${!(
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Active Prime"
                  ) && "reduce_opct_onhover"
                    }`}
                  onClick={() => setIsDraftOpen(true)}
                  disabled={
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Active Prime"
                  }
                  style={{
                    whiteSpace: "nowrap",
                    width: 110,
                    background: theme.color.editButton,
                    color: "white",
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    undo_icon
                  </Icon>
                  Revert
                </Button>
              </FlexGrow>
              <FlexGrow style={{ flexGrow: 0 }}>
                <Button
                  className={`${!(
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Draft"
                  ) && "reduce_opct_onhover"
                    }`}
                  onClick={() => setIsEditOpen(true)}
                  disabled={
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Draft"
                  }
                  style={{
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                    width: 110,
                    whiteSpace: "nowrap",
                    background: theme.color.editButton,
                    color: "white",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    edit
                  </Icon>
                  Edit
                </Button>
              </FlexGrow>
              <FlexGrow style={{ flexGrow: 0 }}>
                <Button
                  className={`${!(
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Draft"
                  ) && "reduce_opct_onhover"
                    }`}
                  onClick={() => setIsDeleteOpen(true)}
                  disabled={
                    selectedVersion.title === "" ||
                    selectedVersion.university === "" ||
                    selectedVersion.status !== "Draft"
                  }
                  style={{
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                    width: 110,
                    whiteSpace: "nowrap",
                    background: theme.color.deleteButton,
                    color: "white",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    delete_outline_icon
                  </Icon>
                  Delete
                </Button>
              </FlexGrow>

              <FlexGrow style={{ textAlign: "end" }}>
                <Button
                  disabled={version.version_number === 0}
                  className={
                    version.version_number === 0 ? "" : "reduce_opct_onhover"
                  }
                  onClick={() => setImportOpen(true)}
                  style={{
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                    width: 110,
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    background: theme.color.greenButton,
                    color: "white",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    upload{" "}
                  </Icon>
                  Import
                </Button>
                <Button
                  disabled={version.version_number === 0}
                  className={
                    version.version_number === 0 ? "" : "reduce_opct_onhover"
                  }
                  onClick={() => CreateUrl()}
                  style={{
                    marginLeft: 10,
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                    width: 110,
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    background: theme.color.greenButton,
                    color: "white",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    download
                  </Icon>
                  Export
                </Button>

                <Button
                  className="reduce_opct_onhover"
                  onClick={() => setIsCreateOpen(true)}
                  style={{
                    marginLeft: 10,
                    boxShadow: "0 2px 5px #808080cc",
                    border: "none",
                    width: 110,
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    background: theme.color.blueButton,
                    color: "white",
                  }}
                >
                  <Icon fontSize="small" style={{ paddingRight: 10 }}>
                    add_circle_outline_icon
                  </Icon>
                  Create
                </Button>
              </FlexGrow>
            </>
          )}
        </CustomBox>
      )}
      {!showModal && (
        <TableComponent
          style={{ backgroundColor: "white" }}
          // navHeight="165px"
          aria-label="sticky table"
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <>
                  <StyledHeaderCell
                    key={column.id}
                    align={column.align}
                    backgroundColor={theme.color.lightBlueGray50}
                    color={theme.color.lightBlueGray800}
                    style={{ minWidth: column.minWidth , paddingLeft: column.id === "title" ? 80 : "" }}
                  >
                    {column.label}
                  </StyledHeaderCell>
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderSectionHeader(
              "Historical Versions",
              groupedData.historical.length,
              expandedSections.historical,
              "history",
              "#000000",
              "#ecf5fe",
              "#0080c033",
              () => toggleSection('historical')
            )}
            {expandedSections.historical && renderVersionRows(groupedData.historical)}
            
            <div style={{ marginBottom: 5 }} />

            {renderSectionHeader(
              "Active Legacy Versions",
              groupedData.legacy.length,
              expandedSections.legacy,
              "verified",
              "#000000",
              "#ecf5fe",
              "#0080c033",
              () => toggleSection('legacy')
            )}
            {expandedSections.legacy && renderVersionRows(groupedData.legacy)}
            <div style={{ marginBottom: 5 }} />

            {renderSectionHeader(
              "Active Versions",
              groupedData.active.length,
              expandedSections.active,
              "check_circle",
              "#000000",
              "#ecf5fe",
              "#0080c033",
              () => toggleSection('active')
            )}
            {expandedSections.active && renderVersionRows(groupedData.active)}
            <div style={{ marginBottom: 5 }} />

            {renderSectionHeader(
              "Draft Versions",
              groupedData.draft.length,
              expandedSections.draft,
              "edit_note",
              "#000000",
              "#ecf5fe",
              "#0080c033",
              () => toggleSection('draft')
            )}
            {expandedSections.draft && renderVersionRows(groupedData.draft)}
          </TableBody>
        </TableComponent>
      )}
      {(loading || versionContext.selectLoading) && (
        <div className="overlay">
          <div className="overlay-content"></div>
        </div>
      )}
    </>
  );
}
export default AllVersionsList;
